import { useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { AlertCircle, Eye, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import type { Customer, CustomerListProps, CustomerFilters } from '@repo/types';
import { CustomerFilterBar } from './CustomerFilterBar';
import { useCustomers } from '@/lib/hooks/useCustomers';
import { humanize } from './utils';

export function CustomerList({ showMatchScore = false, onAssignJob }: CustomerListProps) {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState<CustomerFilters>({});

  // Convert CustomerFilters to API filters
  const apiFilters = {
    search: filters.search,
    location: filters.location,
    subscriptionTier: filters.subscriptionTier,
    subscriptionStatus: filters.subscriptionStatus,
    careerLevel: filters.careerLevel,
    jobSearchStatus: filters.jobSearchStatus,
    workAuthorization: filters.workAuthorization,
    availabilityStatus: filters.availabilityStatus,
    minExperience: filters.minExperience,
    maxExperience: filters.maxExperience,
  };

  const { data: customersData, isLoading, error } = useCustomers(apiFilters, page, pageSize);
  const customers = customersData?.data || [];

  const [sorting, setSorting] = useState<SortingState>([]);

  // Reset to page 1 when filters change.
  const handleFiltersChange = useCallback((newFilters: CustomerFilters) => {
    setFilters(newFilters);
    setPage(1);
  }, []);

  const columns: ColumnDef<Customer>[] = [
    ...(showMatchScore
      ? [
          {
            accessorKey: 'match_score',
            header: ({ column }) => <DataTableColumnHeader column={column} label="Match" />,
            cell: ({ row }) => {
              const score = row.getValue('match_score') as number;
              if (!score) return '-';
              const percentage = Math.round(score * 100);
              const color =
                percentage >= 80 ? 'bg-emerald-500' : percentage >= 60 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <div className={`size-2 rounded-full ${color}`} />
                  <span className="font-medium">{percentage}%</span>
                </div>
              );
            },
          } as ColumnDef<Customer>,
        ]
      : []),
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} label="Name" />,
      cell: ({ row }) => <span className="font-medium">{row.getValue('name')}</span>,
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => (
        <span className="block max-w-[220px] truncate text-muted-foreground">{row.getValue('email') || '-'}</span>
      ),
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      cell: ({ row }) => <span className="whitespace-nowrap">{row.getValue('phone') || '-'}</span>,
    },
    {
      accessorKey: 'gender',
      header: 'Gender',
      cell: ({ row }) => humanize(row.getValue('gender')) || '-',
    },
    {
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }) => row.getValue('location') || '-',
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="size-8" title="View details" asChild>
            <Link to="/admin/customers/$id" params={{ id: row.original.id }}>
              <Eye className="size-4" />
              <span className="sr-only">View details</span>
            </Link>
          </Button>
          {onAssignJob && (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => onAssignJob(row.original.id)}
              title="Assign job"
            >
              <UserPlus className="size-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: customers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
    manualPagination: true, // Server-side pagination
    pageCount: customersData?.totalPages || 0,
  });

  return (
    <div className="space-y-4">
      <CustomerFilterBar filters={filters} onFiltersChange={handleFiltersChange} />

      {isLoading ? (
        <DataTableSkeleton columns={6} />
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>Failed to load customers. Please try again.</AlertDescription>
        </Alert>
      ) : (
        <>
          <DataTable
            table={table}
            emptyState={
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Users className="size-6 text-muted-foreground" />
                  </EmptyMedia>
                  <EmptyTitle>No customers found</EmptyTitle>
                  <EmptyDescription>Try adjusting your filters or search terms.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            }
          />

          {customersData && customersData.total > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-medium text-foreground">{(page - 1) * pageSize + 1}</span>–
                <span className="font-medium text-foreground">
                  {Math.min(page * pageSize, customersData.total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{customersData.total}</span> customers
              </p>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {customersData.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(customersData.totalPages, p + 1))}
                    disabled={page >= customersData.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
